import React, {memo, useEffect, useRef} from 'react';
import {twMerge} from 'tailwind-merge';
import PopoverContent from 'components/ui/Popover/PopoverContent';
import {useMenuKeyboard} from 'components/ui/Menu/useMenuKeyboard';
import {usePopoverContext} from 'components/ui/Popover';

/**
 * DropdownMenuContent - The content container for DropdownMenu
 * Uses PopoverContent as base and adds keyboard navigation and menu-specific features
 *
 * @typedef {Object} DropdownMenuContentProps
 * @property {React.ReactNode} children - Menu items
 * @property {Array} items - Array of items (for keyboard navigation)
 * @property {number} highlightedIndex - Currently highlighted item index
 * @property {Function} setHighlightedIndex - Function to set highlighted index
 * @property {Function} onItemActivate - Callback when item is activated
 * @property {Function} [getItemDisabled] - Function to check if item is disabled
 * @property {string} [size] - Size variant for menu items: 'xs', 'sm', 'md', 'lg' (default: 'md')
 * @property {boolean} [autoHighlightFirst] - When true, auto-highlight first enabled item on open
 * @property {string} [className] - Additional CSS classes
 *
 * Note: `children` may include non-menu elements (e.g. labels, separators). Only elements with
 * role="menuitem" (or DropdownMenuItem) participate in keyboard navigation indexing.
 */
const DropdownMenuContent = memo(
	({
		children,
		items = [],
		highlightedIndex = -1,
		setHighlightedIndex,
		onItemActivate,
		getItemDisabled,
		size = 'md',
		autoHighlightFirst = false,
		className,
		...props
	}) => {
		const {open, isClosing, setOpen, setIsClosing} = usePopoverContext();
		const contentRef = useRef(null);

		// Keyboard navigation
		const handleKeyDown = useMenuKeyboard({
			open,
			isClosing,
			items,
			highlightedIndex,
			setHighlightedIndex,
			onItemActivate,
			onEscape: () => {
				setIsClosing(true);
				setOpen(false);
				setTimeout(() => setIsClosing(false), 150);
			},
			getItemDisabled,
		});

		// Focus management: focus menu content when opened
		useEffect(() => {
			if (open && contentRef.current) {
				// Small delay to ensure DOM is ready
				setTimeout(() => {
					contentRef.current?.focus();
				}, 0);
			}
		}, [open]);

		// Auto-highlight first enabled item on open
		useEffect(() => {
			if (!open || !autoHighlightFirst || !setHighlightedIndex) return;
			let firstIndex = 0;
			if (Array.isArray(items) && items.length > 0) {
				for (let i = 0; i < items.length; i++) {
					const disabled =
						typeof getItemDisabled === 'function'
							? !!getItemDisabled(items[i], i)
							: false;
					if (!disabled) {
						firstIndex = i;
						break;
					}
				}
			}
			setHighlightedIndex(firstIndex);
		}, [open, autoHighlightFirst, items, getItemDisabled, setHighlightedIndex]);

		// Enhanced click handler that combines PopoverContent's default with menu-specific behavior
		const handleClick = e => {
			// PopoverContent's default stops propagation to prevent closing
			e.stopPropagation();
		};

		return (
			<PopoverContent
				className={twMerge('p-1 min-w-[160px]', className)}
				onClick={handleClick}
				{...props}
			>
				<div
					ref={contentRef}
					role="menu"
					aria-orientation="vertical"
					aria-activedescendant={
						items[highlightedIndex] ? `dropdown-menu-item-${highlightedIndex}` : undefined
					}
					className="focus:outline-none"
					onKeyDown={handleKeyDown}
					tabIndex={-1}
				>
					{/* Important: keyboard navigation indices are based on `items` (menu items only),
					    so we must not use raw child index when injecting IDs/highlight. */}
					{(() => {
						let menuItemIndex = -1;
						return React.Children.map(children, child => {
							if (React.isValidElement(child)) {
								const isMenuItem =
									child.type?.displayName === 'DropdownMenuItem' ||
									child.props?.role === 'menuitem';

								if (isMenuItem) {
									menuItemIndex += 1;
									return React.cloneElement(child, {
										highlighted: menuItemIndex === highlightedIndex,
										index: menuItemIndex,
										size,
									});
								}
							}
							return child;
						});
					})()}
				</div>
			</PopoverContent>
		);
	},
);

DropdownMenuContent.displayName = 'DropdownMenuContent';

export default DropdownMenuContent;
